import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import '@ts-gql/apollo';
import type { NormalizedCacheObject } from '@apollo/client';
import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  HttpLink,
} from '@apollo/client';

import type { HydratableCredentials } from '@reckon-web/auth-store';
import { useAuth } from '@reckon-web/auth-store';
import { isSSR } from '@reckon-web/next-is-ssr';

import { AnnotateRequestHeadersLink } from './AnnotateRequestHeadersLink';
import { AnnotateRequestHeaderWithCorrelationIdLink } from './AnnotateRequestHeaderWithCorrelationIdLink';
import type { ResultSchema } from './Cache';
import { createCache } from './Cache';
import type { AuthErrorRecovery } from './HandleAuthErrorResponseLink';
import { HandleAuthErrorResponseLink } from './HandleAuthErrorResponseLink';
import { OperationsListLink } from './OperationsListLink';
import { createPrefixLink } from './PrefixLink';
import type { APITrace } from './types';
import { gqlApiClientProviderEvents } from './GQLApiClientProviderEvents';
import type { SuccessfulGqlMutationCallback } from './successfulMutationTracking';

/**
 * Exchanges a refresh token for new credentials, which must carry the
 * `accessToken` the next requests are signed with.
 *
 * Run the exchange on `client` with `context: skipAuthErrorHandlingContext`.
 * That client's link chain is the one that called this handler, so a refresh
 * that itself fails with UNAUTHENTICATED would otherwise be routed straight
 * back into another refresh.
 */
export type TokenRefreshHandler = (input: {
  client: ApolloClient<NormalizedCacheObject>;
  refreshToken: string;
}) => Promise<HydratableCredentials>;

export type GqlApiClientProviderProps = {
  url: string;
  name: string;
  operationPrefixNameForAddingToRawLogOutputs: string;
  version: string;
  schema: ResultSchema;
  children: React.ReactNode;
  connectToDevTools?: boolean;
  onTokenRefresh?: TokenRefreshHandler;
  isAuthError?: (error?: string) => boolean;
  onOperationStart?: (operation: string) => void;
  onOperationFinish?: (operations: string, apiTraces: Array<APITrace>) => void;
  onSuccessfulMutation?: SuccessfulGqlMutationCallback;
  /**
   * GQL cache will be persisted to local storage if this key is provided.
   * Currently the persisted objects are hard coded to be Payroll auth specific,
   *  if we need this functionality in other apps as well we can look at abstracting
   *  out the cache persistor.
   * */
  cachePersistKey?: string;
  /**
   * Because cache persistence is a very async process, we can never reliably, impreatively
   *  purge it in the middle of a render. Instead, we set a flag that purges it on next app load
   *  so we start with fresh server data.
   */
  cachePersistPurgeOnStartupKey?: string;
};

/**
 * @description
 * =
 * MAKE SURE TO PASS STABLE CALLBACKS TO THIS COMPONENT
 * =
 * or it will cause the gql client to be recreated causing full page reloads.
 */
/**
 * Context that holds the cache persistor's `purge` function.
 * This is used by `useInvalidateApolloPersistedCache` to clear persisted cache from localStorage.
 * @internal
 */
export const GqlCachePurgeContext = React.createContext<
  (() => Promise<void>) | undefined
>(undefined);

export const GqlApiClientProvider = ({
  url,
  name,
  operationPrefixNameForAddingToRawLogOutputs,
  version,
  schema,
  children,
  cachePersistKey,
  cachePersistPurgeOnStartupKey,
  connectToDevTools,
  onTokenRefresh,
  isAuthError,
  onOperationStart,
  onOperationFinish,
  onSuccessfulMutation,
}: GqlApiClientProviderProps) => {
  const [apolloClient, setApolloClient] = useState<any>();
  const [cachePurge, setCachePurge] = useState<
    (() => Promise<void>) | undefined
  >();
  const auth = useAuth();

  const requestHeadersRef = useRef<Record<string, string> | undefined>(
    auth.requestHeaders
  );
  const apolloClientRef = useRef<
    ApolloClient<NormalizedCacheObject> | undefined
  >(undefined);

  const pendingRecoveryRef = useRef<Promise<AuthErrorRecovery> | undefined>(
    undefined
  );

  const recoverSession = useCallback(async (): Promise<AuthErrorRecovery> => {
    if (!onTokenRefresh || !auth.credentials?.refreshToken) {
      auth.removeCredentials();
      auth.logout({ reasonCode: 'INVALID_TOKEN' });
      return 'session-not-recovered';
    }

    if (apolloClientRef.current) {
      try {
        const newCredentials = await onTokenRefresh({
          client: apolloClientRef.current,
          refreshToken: auth.credentials.refreshToken,
        });

        if (newCredentials) {
          auth.setCredentials(newCredentials);
          // The link replays the failed operation as soon as this resolves,
          // which is before React has committed the new credentials and the
          // syncRequestHeaders effect has run. Sign the replay ourselves or it
          // goes out with the token that just expired.
          requestHeadersRef.current = mergeAndPruneRequestHeaders(
            requestHeadersRef.current,
            auth.createRequestHeaders(newCredentials)
          );
          return 'session-recovered';
        } else {
          throw new Error('no credentials');
        }
      } catch {
        auth.removeCredentials();
        auth.logout({ reasonCode: 'INVALID_TOKEN' });
      }
    }

    return 'session-not-recovered';
  }, [
    auth.credentials?.refreshToken,
    auth.createRequestHeaders,
    auth.logout,
    onTokenRefresh,
    auth.removeCredentials,
    auth.setCredentials,
  ]);

  /**
   * Operations that fail with UNAUTHENTICATED while a recovery is already
   * running share that recovery, so N concurrent failures cost one token
   * refresh (or one logout), not N. With refresh-token rotation, N refreshes
   * would have the later ones fail and sign the user out right after the first
   * succeeded.
   */
  const handleUnauthenticatedError = useCallback((): Promise<
    AuthErrorRecovery
  > => {
    if (pendingRecoveryRef.current) {
      return pendingRecoveryRef.current;
    }

    const recovery = recoverSession().finally(() => {
      pendingRecoveryRef.current = undefined;
    });
    pendingRecoveryRef.current = recovery;

    return recovery;
  }, [recoverSession]);

  const routeAuthError = useCallback(
    async (
      error?: 'UNAUTHENTICATED' | Error | string | undefined
    ): Promise<AuthErrorRecovery> => {
      if (error === 'UNAUTHENTICATED') {
        return handleUnauthenticatedError();
      }

      return 'session-not-recovered';
    },
    [handleUnauthenticatedError]
  );

  const prefixLink = useMemo(() => {
    return createPrefixLink({
      name: operationPrefixNameForAddingToRawLogOutputs,
    });
  }, [operationPrefixNameForAddingToRawLogOutputs]);

  /**
   * @description
   * =
   * MAKE SURE TO PASS STABLE CALLBACKS TO THIS COMPONENT
   * =
   * or it will cause the gql client to be recreated causing full page reloads.
   */
  useEffect(() => {
    async function createApolloClient() {
      recordReloadCount();

      const cache = await createCache({
        schema,
        cachePersistKey,
        cachePersistPurgeOnStartupKey,
      });

      const client = new ApolloClient({
        name,
        version,
        cache: cache.cache,
        credentials: 'include',
        connectToDevTools,
        link: ApolloLink.from([
          prefixLink,
          OperationsListLink({
            operationPrefixNameForAddingToRawLogOutputs,
            onOperationStart,
            onOperationFinish,
            onSuccessfulMutation,
          }),
          HandleAuthErrorResponseLink(routeAuthError, isAuthError),
          AnnotateRequestHeaderWithCorrelationIdLink(),
          AnnotateRequestHeadersLink({ requestHeadersRef }),
          new HttpLink({
            uri: url,
            fetch:
              typeof window === 'undefined'
                ? () => {
                    return new Promise(() => {});
                  }
                : fetch,
          }),
        ]),
      });
      apolloClientRef.current = client;
      if (cache.persistor) {
        client.onClearStore(async () => {
          await cache.persistor.purge();
        });
        client.onResetStore(async () => {
          await cache.persistor.purge();
        });
        await cache.persistor.restore();
        setCachePurge(() => {
          return () => {
            return cache.persistor.purge();
          };
        });
      }
      setApolloClient(client);
    }

    createApolloClient();
  }, [
    cachePersistKey,
    cachePersistPurgeOnStartupKey,
    connectToDevTools,
    isAuthError,
    name,
    onOperationFinish,
    onOperationStart,
    onSuccessfulMutation,
    prefixLink,
    routeAuthError,
    schema,
    url,
    version,
  ]);

  useEffect(
    function syncRequestHeaders() {
      requestHeadersRef.current = mergeAndPruneRequestHeaders(
        requestHeadersRef.current,
        auth.requestHeaders
      );
    },
    [auth.requestHeaders]
  );

  useEffect(function listenTo_requestHeadersUpdatedEvent() {
    const unsub = gqlApiClientProviderEvents.subscribe(
      'requestHeadersUpdate',
      (args) => {
        requestHeadersRef.current = mergeAndPruneRequestHeaders(
          requestHeadersRef.current,
          args.requestHeaders
        );
      }
    );

    return () => {
      unsub();
    };
  }, []);

  if (isSSR()) {
    return <></>;
  }

  if (!apolloClient) {
    return null;
  }

  return (
    <GqlCachePurgeContext.Provider value={cachePurge}>
      <ApolloProvider client={apolloClient}>{children}</ApolloProvider>
    </GqlCachePurgeContext.Provider>
  );
};

const mergeAndPruneRequestHeaders = (
  ...args: Array<Record<string, string | null | undefined> | undefined>
) => {
  const headers: Record<string, string> = {};

  for (const argsHeaders of args) {
    if (argsHeaders === undefined) {
      continue;
    }
    for (const [key, value] of Object.entries(argsHeaders)) {
      if (value !== null && value !== undefined) {
        headers[key] = value;
      } else {
        delete headers[key];
      }
    }
  }

  return headers;
};

function recordReloadCount() {
  if (typeof window === 'undefined') {
    return;
  }

  if (typeof window.reckon_gql_client_count === 'undefined') {
    window.reckon_gql_client_count = 1;
    return;
  }

  window.reckon_gql_client_count += 1;

  // React StrictMode remounts providers during local development, so allow the
  // first duplicate client before flagging likely real recreation issues.
  if (window.reckon_gql_client_count <= 2) {
    return;
  }

  console.error(
    `%c============= ATTENTION: GQL client recreated =============
The GQL client has been created more than once during this session.
This should only happen if you've made changes locally to the gql client code.
It should never happen when deployed as it will cause performance and UX problems.`,
    'background: #fbd7d9; color: #E30613'
  );
}

declare global {
  interface Window {
    reckon_gql_client_count?: number;
  }
}
